
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserProviderProductOrganisation from 'Models/Security/Acl/UserProviderProductOrganisation';
import VisitorsProviderProductOrganisation from 'Models/Security/Acl/VisitorsProviderProductOrganisation';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IProviderProductOrganisationEntityAttributes extends IModelAttributes {
	price: number;
	customMarkup: number;
	discountRate: number;
	recomended: boolean;
	isEnabled: boolean;

	businessEntityReportSettingss: Array<
		| Models.BusinessEntityCreditReportSettingsEntity
		| Models.IBusinessEntityCreditReportSettingsEntityAttributes
	>;
	organisationId: string;
	organisation: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	providerProductId: string;
	providerProduct: Models.ProviderProductEntity | Models.IProviderProductEntityAttributes;

}

@entity('ProviderProductOrganisationEntity', 'Provider Product Organisation')
export default class ProviderProductOrganisationEntity extends Model
	implements IProviderProductOrganisationEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserProviderProductOrganisation(),
		new VisitorsProviderProductOrganisation(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	@Validators.Numeric()
	@observable
	@attribute<ProviderProductOrganisationEntity, number>()
	@CRUD({
		name: 'Price',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public price: number;
	

	
	@Validators.Numeric()
	@observable
	@attribute<ProviderProductOrganisationEntity, number>()
	@CRUD({
		name: 'Custom Markup',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public customMarkup: number;
	

	
	/**
	 * Value from 0.0 to 1.0
	 */
	@Validators.Numeric()
	@observable
	@attribute<ProviderProductOrganisationEntity, number>()
	@CRUD({
		name: 'Discount Rate',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public discountRate: number;

	@observable
	@attribute<ProviderProductOrganisationEntity, boolean>()
	@CRUD({
		name: 'Recomended',
		displayType: 'checkbox',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public recomended: boolean;
	

	@observable
	@attribute<ProviderProductOrganisationEntity, boolean>()
	@CRUD({
		name: 'Is Enabled',
		displayType: 'checkbox',
		order: 60,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isEnabled: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Business Entity Report Settingss',
		displayType: 'reference-multicombobox',
		order: 80,
		referenceTypeFunc: () => Models.BusinessEntityCreditReportSettingsEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'businessEntityReportSettingss',
			oppositeEntity: () => Models.BusinessEntityCreditReportSettingsEntity,
		}),
		
	})
	public businessEntityReportSettingss: Models.BusinessEntityCreditReportSettingsEntity[] = [];

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 90,
		referenceTypeFunc: () => Models.OrganisationEntity,
		
	})
	public organisationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Provider Product',
		displayType: 'reference-combobox',
		order: 100,
		referenceTypeFunc: () => Models.ProviderProductEntity,
		
	})
	public providerProductId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public providerProduct: Models.ProviderProductEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IProviderProductOrganisationEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IProviderProductOrganisationEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.price !== undefined) {
				this.price = attributes.price;
			}
			if (attributes.customMarkup !== undefined) {
				this.customMarkup = attributes.customMarkup;
			}
			if (attributes.discountRate !== undefined) {
				this.discountRate = attributes.discountRate;
			}
			if (attributes.recomended !== undefined) {
				this.recomended = attributes.recomended;
			}
			if (attributes.isEnabled !== undefined) {
				this.isEnabled = attributes.isEnabled;
			}
			if (attributes.businessEntityReportSettingss !== undefined && Array.isArray(attributes.businessEntityReportSettingss)) {
				for (const model of attributes.businessEntityReportSettingss) {
					if (model instanceof Models.BusinessEntityCreditReportSettingsEntity) {
						this.businessEntityReportSettingss.push(model);
					} else {
						this.businessEntityReportSettingss.push(new Models.BusinessEntityCreditReportSettingsEntity(model));
					}
				}
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}
			if (attributes.providerProductId !== undefined) {
				this.providerProductId = attributes.providerProductId;
			}
			if (attributes.providerProduct !== undefined) {
				if (attributes.providerProduct === null) {
					this.providerProduct = attributes.providerProduct;
				} else if (attributes.providerProduct instanceof Models.ProviderProductEntity) {
					this.providerProduct = attributes.providerProduct;
					this.providerProductId = attributes.providerProduct.id;
				} else {
					this.providerProduct = new Models.ProviderProductEntity(attributes.providerProduct);
					this.providerProductId = this.providerProduct.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		businessEntityReportSettingss {
			${Models.BusinessEntityCreditReportSettingsEntity.getAttributes().join('\n')}
		}
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
			${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
		}
		providerProduct {
			${Models.ProviderProductEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			businessEntityReportSettingss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ProviderProductOrganisationEntity.prototype, 'created');
CRUD(modifiedAttr)(ProviderProductOrganisationEntity.prototype, 'modified');
